import React from 'react';

interface PageHeaderAction {
  label: string;
  onClick: () => void;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
}

interface PageHeaderProps {
  /** Monospace label above the title (e.g. "Dashboard", "Library") */
  label?: string;
  /** Main heading */
  title: string;
  /** Subtitle below the title */
  description?: string;
  /** Action buttons on the right side */
  actions?: PageHeaderAction[];
}

const actionVariantClasses: Record<string, string> = {
  primary: 'btn btn-primary',
  secondary: 'btn btn-secondary',
  ghost: 'btn btn-ghost',
};

const PageHeader: React.FC<PageHeaderProps> = ({
  label,
  title,
  description,
  actions = [],
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div>
        {label && (
          <p className="font-mono text-label-md text-tertiary uppercase tracking-widest mb-1">
            {label}
          </p>
        )}
        <h1 className="text-2xl font-display font-bold text-on-surface tracking-tight">
          {title}
        </h1>
        {description && (
          <p className="text-on-surface-variant/50 mt-1 text-sm">
            {description}
          </p>
        )}
      </div>
      {actions.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`${actionVariantClasses[action.variant || 'primary']} gap-2 px-4 sm:px-6 w-full sm:w-auto justify-center sm:justify-start`}
            >
              {action.icon && (
                <span className="material-icons text-sm">{action.icon}</span>
              )}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PageHeader;